import { useState, useEffect, useCallback, useRef } from "react";
import { Lock, Unlock, ChevronDown, Palette, RotateCcw, Sliders, Menu, X, Check } from "lucide-react";

let NAMED_COLORS = [];
let NAMED_RGB = [];

function parseColorData(data) {
  NAMED_COLORS = data.map(c => [c.name, c.hex.toLowerCase()]);
  NAMED_RGB = NAMED_COLORS.map(([name, hex]) => [
    name,
    parseInt(hex.slice(1,3),16),
    parseInt(hex.slice(3,5),16),
    parseInt(hex.slice(5,7),16),
  ]);
}

function loadColorLibrary() {
  return fetch('/colornames.json')
    .then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
      return r.json();
    })
    .then(data => {
      parseColorData(data);
      console.log(`Loaded ${NAMED_COLORS.length} colors from library`);
      return NAMED_COLORS;
    });
}

function randHex() {
  return '#' + Array.from({length:6}, () => '0123456789abcdef'[Math.floor(Math.random()*16)]).join('');
}

function getColorName(hex) {
  if (!NAMED_RGB.length) return "";
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  let min = Infinity, best = "";
  for (const [name, cr, cg, cb] of NAMED_RGB) {
    const d = (r-cr)**2 + (g-cg)**2 + (b-cb)**2;
    if (d < min) { min = d; best = name; }
  }
  return best;
}

function randFromLibrary() {
  if (!NAMED_COLORS.length) return randHex();
  return NAMED_COLORS[Math.floor(Math.random() * NAMED_COLORS.length)][1];
}

function hexToHsl(hex) {
  let r = parseInt(hex.slice(1,3),16)/255, g = parseInt(hex.slice(3,5),16)/255, b = parseInt(hex.slice(5,7),16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b), l = (max+min)/2;
  let h=0, s=0;
  if (max !== min) {
    const d = max-min;
    s = l > 0.5 ? d/(2-max-min) : d/(max+min);
    if (max===r) h = ((g-b)/d + (g<b?6:0))/6;
    else if (max===g) h = ((b-r)/d+2)/6;
    else h = ((r-g)/d+4)/6;
  }
  return [h*360, s*100, l*100];
}

function hslToHex(h, s, l) {
  h = ((h%360)+360)%360; s /= 100; l /= 100;
  const a = s * Math.min(l, 1-l);
  const f = n => { const k = (n + h/30) % 12; return l - a * Math.max(Math.min(k-3, 9-k, 1), -1); };
  return '#' + [f(0),f(8),f(4)].map(x => Math.round(x*255).toString(16).padStart(2,'0')).join('');
}

function snapToLibrary(hex) {
  if (!NAMED_RGB.length) return hex;
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  let min = Infinity, best = hex;
  for (let i = 0; i < NAMED_RGB.length; i++) {
    const [, cr, cg, cb] = NAMED_RGB[i];
    const d = (r-cr)**2 + (g-cg)**2 + (b-cb)**2;
    if (d < min) { min = d; best = NAMED_COLORS[i][1]; }
  }
  return best;
}

function hexToRgb(hex) {
  return [parseInt(hex.slice(1,3),16), parseInt(hex.slice(3,5),16), parseInt(hex.slice(5,7),16)];
}

function rgbToHex(r,g,b) {
  return '#' + [r,g,b].map(x => Math.max(0,Math.min(255,Math.round(x))).toString(16).padStart(2,'0')).join('');
}

function isLight(hex) {
  const [r,g,b] = hexToRgb(hex);
  return (r*299 + g*587 + b*114) / 1000 > 140;
}

const METHODS = ["Random","Monochromatic","Analogous","Complementary","Split Complementary","Triadic","Tetradic","Square"];

function generatePalette(count, method, locked) {
  const colors = new Array(count).fill(null);
  locked.forEach((c,i) => { if (i < count && c) colors[i] = c; });
  const baseIdx = colors.findIndex(c => c !== null);
  const base = baseIdx >= 0 ? colors[baseIdx] : randFromLibrary();
  const [bH, bS, bL] = hexToHsl(base);
  const needed = [];
  colors.forEach((c,i) => { if (!c) needed.push(i); });
  
  // Helper to create varied lightness/saturation across palette
  const varyLS = (i, total, sRange = 30, lRange = 45) => {
    const pos = total > 1 ? i / (total - 1) : 0.5;
    const lShift = (pos - 0.5) * lRange * 2 + (Math.random() - 0.5) * 15;
    const sShift = (Math.random() - 0.5) * sRange;
    return [
      Math.max(20, Math.min(100, bS + sShift)),
      Math.max(15, Math.min(90, bL + lShift))
    ];
  };

  if (method === "Random") {
    needed.forEach(i => { colors[i] = randFromLibrary(); });
  } else if (method === "Monochromatic") {
    needed.forEach((idx, i) => {
      const [s, l] = varyLS(i, needed.length, 25, 55);
      const hueShift = (Math.random() - 0.5) * 8; // slight hue drift
      const raw = hslToHex(bH + hueShift, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  } else if (method === "Analogous") {
    const spread = 60; // total spread in degrees
    needed.forEach((idx, i) => {
      const angle = -spread/2 + (spread * (i + 0.5 + (Math.random()-0.5)*0.5)) / needed.length;
      const [s, l] = varyLS(i, needed.length, 35, 40);
      const raw = hslToHex(bH + angle, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  } else if (method === "Complementary") {
    needed.forEach((idx, i) => {
      const side = i % 2 === 0 ? 0 : 180;
      const hueNoise = (Math.random() - 0.5) * 20;
      const [s, l] = varyLS(i, needed.length, 40, 50);
      const raw = hslToHex(bH + side + hueNoise, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  } else if (method === "Split Complementary") {
    const angles = [0, 150, 210];
    needed.forEach((idx, i) => {
      const baseAngle = angles[i % angles.length];
      const hueNoise = (Math.random() - 0.5) * 25;
      const [s, l] = varyLS(i, needed.length, 35, 45);
      const raw = hslToHex(bH + baseAngle + hueNoise, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  } else if (method === "Triadic") {
    const angles = [0, 120, 240];
    needed.forEach((idx, i) => {
      const baseAngle = angles[i % angles.length];
      const hueNoise = (Math.random() - 0.5) * 30;
      const [s, l] = varyLS(i, needed.length, 40, 45);
      const raw = hslToHex(bH + baseAngle + hueNoise, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  } else if (method === "Tetradic") {
    const angles = [0, 60, 180, 240];
    needed.forEach((idx, i) => {
      const baseAngle = angles[i % angles.length];
      const hueNoise = (Math.random() - 0.5) * 25;
      const [s, l] = varyLS(i, needed.length, 35, 45);
      const raw = hslToHex(bH + baseAngle + hueNoise, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  } else if (method === "Square") {
    const angles = [0, 90, 180, 270];
    needed.forEach((idx, i) => {
      const baseAngle = angles[i % angles.length];
      const hueNoise = (Math.random() - 0.5) * 25;
      const [s, l] = varyLS(i, needed.length, 35, 45);
      const raw = hslToHex(bH + baseAngle + hueNoise, s, l);
      colors[idx] = snapToLibrary(raw);
    });
  }
  return colors;
}

function ColorSlider({ label, value, max, color, onChange }) {
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="flex justify-between text-[11px] font-bold mb-1.5 opacity-90">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: color }}></span>
          {label}
        </span>
        <span className="font-mono">{value}</span>
      </div>
      <input type="range" min={0} max={max} value={value} onChange={e => onChange(+e.target.value)}
        className="w-full h-2 cursor-pointer rounded-full appearance-none"
        style={{ 
          background: `linear-gradient(to right, ${color} ${(value/max)*100}%, rgba(128,128,128,0.3) ${(value/max)*100}%)`,
          accentColor: color 
        }} />
    </div>
  );
}

function ColorBar({ color, index, locked, onToggleLock, onColorChange, total, isVertical }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState('');
  const [showSliders, setShowSliders] = useState(false);
  const [copied, setCopied] = useState(false);
  const light = isLight(color);
  const [r,g,b] = hexToRgb(color);
  const inputRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const liveUpdate = (val) => {
    const v = val.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
    setEditVal(v);
    if (v.length === 6) onColorChange('#' + v.toLowerCase());
  };

  const commitEdit = () => {
    const v = editVal.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
    if (v.length === 6) onColorChange('#' + v.toLowerCase());
    setEditing(false);
  };

  const handleBlur = (e) => {
    if (barRef.current && barRef.current.contains(e.relatedTarget)) return;
    commitEdit();
  };

  const copyToClipboard = () => {
    if (editing) return;
    navigator.clipboard.writeText(color.toUpperCase()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const fg = light ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.92)';
  const fgSub = light ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.55)';
  const fgBtn = light ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)';
  const fgBtnHover = light ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)';

  return (
    <div
      ref={barRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={copyToClipboard}
      className={`relative flex items-center justify-center transition-all duration-300 cursor-pointer ${isVertical ? 'w-full min-h-[100px]' : 'h-full min-w-0 flex-1'}`}
      style={{ background: color }}
    >
      {/* Main content - centered */}
      <div className="flex flex-col items-center gap-3">
        {/* RGB Sliders drawer - above controls */}
        <div className={`overflow-hidden transition-all duration-300 ${showSliders ? 'max-h-72 opacity-100 mb-2' : 'max-h-0 opacity-0'}`}>
          <div className="backdrop-blur-xl rounded-2xl p-5 border border-white/20 shadow-xl"
            onClick={e => e.stopPropagation()}
            style={{ background: light ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.5)', color: fg, width: 220 }}>
            <ColorSlider label="R" value={r} max={255} color="#ef4444" onChange={v => onColorChange(rgbToHex(v,g,b))} />
            <ColorSlider label="G" value={g} max={255} color="#22c55e" onChange={v => onColorChange(rgbToHex(r,v,b))} />
            <ColorSlider label="B" value={b} max={255} color="#3b82f6" onChange={v => onColorChange(rgbToHex(r,g,v))} />
          </div>
        </div>

        {/* Controls - above hex code */}
        <div className={`flex items-center gap-2 transition-opacity duration-200 ${hovered || locked || showSliders ? 'opacity-100' : 'opacity-0'}`}>
          <button onClick={(e) => { e.stopPropagation(); onToggleLock(index); }} title={locked ? "Unlock" : "Lock"}
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all backdrop-blur-sm"
            style={{ background: locked ? fg : fgBtn, color: locked ? color : fg }}
            onMouseEnter={e => { if (!locked) e.currentTarget.style.background = fgBtnHover; }}
            onMouseLeave={e => { if (!locked) e.currentTarget.style.background = fgBtn; }}>
            {locked ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); setShowSliders(p => !p); }} title="Adjust RGB"
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all backdrop-blur-sm"
            style={{ background: showSliders ? fg : fgBtn, color: showSliders ? color : fg }}
            onMouseEnter={e => { if (!showSliders) e.currentTarget.style.background = fgBtnHover; }}
            onMouseLeave={e => { if (!showSliders) e.currentTarget.style.background = fgBtn; }}>
            <Sliders size={16} />
          </button>
        </div>

        {/* Color info */}
        <div className="text-center">
          {editing ? (
            <input ref={inputRef} value={editVal} maxLength={6}
              onClick={e => e.stopPropagation()}
              onChange={e => liveUpdate(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={e => { if (e.key==='Enter') commitEdit(); if (e.key==='Escape') { setEditing(false); setShowSliders(false); }}}
              className="bg-transparent border-b-2 outline-none text-center uppercase tracking-widest font-mono font-bold w-28"
              style={{ color: fg, borderColor: fg, fontSize: total <= 5 ? 24 : 18 }}
            />
          ) : (
            <div
              onClick={(e) => { e.stopPropagation(); setEditVal(color.slice(1).toUpperCase()); setEditing(true); }}
              className="font-mono font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-lg transition-all cursor-text"
              style={{ color: fg, fontSize: isVertical ? 18 : (total <= 5 ? 24 : total <= 8 ? 18 : 14) }}
              onMouseEnter={e => e.currentTarget.style.background = fgBtn}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {copied ? <span className="flex items-center gap-1.5"><Check size={16} /> Copied!</span> : color.slice(1).toUpperCase()}
            </div>
          )}
          <div className="font-medium tracking-wide text-xs mt-2" style={{ color: fgSub }}>
            {getColorName(color)}
          </div>
        </div>
      </div>

      {/* Locked badge - always visible when locked */}
      {locked && !hovered && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm"
          style={{ background: fgBtn, color: fgSub }}>
          <Lock size={10} /> Locked
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [count, setCount] = useState(5);
  const [method, setMethod] = useState("Random");
  const [colors, setColors] = useState([]);
  const [locked, setLocked] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load color library on mount
  useEffect(() => {
    loadColorLibrary().then(() => {
      setLoading(false);
      const init = generatePalette(5, "Random", []);
      setColors(init);
      setLocked(new Array(5).fill(false));
    }).catch(err => {
      console.error('Failed to load color library:', err);
      setLoading(false);
      const init = Array.from({length:5}, () => randHex());
      setColors(init);
      setLocked(new Array(5).fill(false));
    });
  }, []);

  const generate = useCallback(() => {
    const lockedColors = locked.map((l, i) => l ? colors[i] : null);
    const newColors = generatePalette(count, method, lockedColors);
    setColors(newColors);
    setLocked(prev => {
      const n = new Array(count).fill(false);
      prev.forEach((v, i) => { if (i < count) n[i] = v; });
      return n;
    });
  }, [count, method, locked, colors]);

  useEffect(() => {
    const handler = (e) => {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault(); generate();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [generate]);

  const toggleLock = (i) => setLocked(p => { const n = [...p]; n[i] = !n[i]; return n; });
  const changeColor = (i, hex) => setColors(p => { const n = [...p]; n[i] = hex; return n; });

  const adjustCount = (newCount) => {
    const c = Math.max(2, Math.min(10, newCount));
    setCount(c);
    setColors(prev => c <= prev.length ? prev.slice(0, c) : [...prev, ...Array.from({length: c - prev.length}, () => randFromLibrary())]);
    setLocked(prev => c <= prev.length ? prev.slice(0, c) : [...prev, ...new Array(c - prev.length).fill(false)]);
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-zinc-950 font-sans">
        <div className="text-center">
          <Palette size={48} className="text-violet-500 mx-auto mb-4" />
          <div className="text-violet-400 text-base font-semibold">Loading color library...</div>
          <div className="text-zinc-600 text-sm mt-2">Preparing 30,000+ named colors</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-zinc-950 overflow-hidden font-sans">
      {/* Top bar */}
      <div className="h-14 flex items-center justify-between px-3 sm:px-5 bg-zinc-950/95 border-b border-white/10 backdrop-blur-xl z-20 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <Palette size={20} className="text-violet-500" />
          <span className="text-white font-bold text-base sm:text-lg tracking-tight">Color Generator</span>
          <span className="text-zinc-600 text-[10px] sm:text-xs font-medium hidden sm:inline">{NAMED_COLORS.length.toLocaleString()} colors</span>
        </div>
        
        {/* Desktop controls */}
        <div className="hidden md:flex items-center gap-3">
          {/* Method dropdown */}
          <div className="relative">
            <button onClick={() => setShowMenu(p => !p)}
              className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-zinc-200 text-sm font-medium flex items-center gap-2 hover:bg-white/15 transition-colors">
              {method} <ChevronDown size={14} />
            </button>
            {showMenu && (
              <>
                <div onClick={() => setShowMenu(false)} className="fixed inset-0 z-30" />
                <div className="absolute top-full mt-1 right-0 bg-zinc-900 border border-white/15 rounded-xl shadow-2xl z-40 min-w-[200px] p-1">
                  {METHODS.map(m => (
                    <button key={m} onClick={() => { setMethod(m); setShowMenu(false); }}
                      className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${m === method ? 'bg-violet-500/20 text-violet-400' : 'text-zinc-300 hover:bg-white/10'}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Count controls */}
          <div className="flex items-center gap-1">
            <button onClick={() => adjustCount(count - 1)}
              className="w-8 h-8 rounded-md border border-white/15 bg-white/10 text-zinc-300 text-lg flex items-center justify-center hover:bg-white/15 transition-colors">−</button>
            <span className="text-white font-bold text-sm min-w-[24px] text-center">{count}</span>
            <button onClick={() => adjustCount(count + 1)}
              className="w-8 h-8 rounded-md border border-white/15 bg-white/10 text-zinc-300 text-lg flex items-center justify-center hover:bg-white/15 transition-colors">+</button>
          </div>
          
          {/* Generate button */}
          <button onClick={generate}
            className="bg-gradient-to-br from-violet-500 to-violet-700 rounded-lg px-4 py-2 text-white text-sm font-semibold flex items-center gap-2 hover:scale-105 transition-transform shadow-lg shadow-violet-500/30">
            <RotateCcw size={14} /> Generate
          </button>
        </div>

        {/* Mobile menu button */}
        <button onClick={() => setMobileMenuOpen(p => !p)} className="md:hidden p-2 text-zinc-300 hover:bg-white/10 rounded-lg transition-colors">
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-zinc-900/98 border-b border-white/10 p-4 flex-shrink-0 z-10">
          <div className="flex flex-col gap-3">
            {/* Method selection */}
            <div>
              <label className="text-zinc-400 text-xs font-medium mb-1.5 block">Harmony Method</label>
              <select value={method} onChange={e => setMethod(e.target.value)}
                className="w-full bg-zinc-800 border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm">
                {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            
            {/* Count + Generate row */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-zinc-400 text-xs font-medium mb-1.5 block">Color</label>
                <div className="flex items-center gap-2">
                  <button onClick={() => adjustCount(count - 1)}
                    className="w-10 h-10 rounded-lg bg-zinc-800 text-zinc-300 text-xl flex items-center justify-center">−</button>
                  <span className="text-white font-bold text-lg flex-1 text-center">{count}</span>
                  <button onClick={() => adjustCount(count + 1)}
                    className="w-10 h-10 rounded-lg bg-zinc-800 text-zinc-300 text-xl flex items-center justify-center">+</button>
                </div>
              </div>
              <div className="flex-1">
                <label className="text-zinc-400 text-xs font-medium mb-1.5 block">&nbsp;</label>
                <button onClick={() => { generate(); setMobileMenuOpen(false); }}
                  className="w-full h-10 bg-gradient-to-br from-violet-500 to-violet-700 rounded-lg text-white font-semibold flex items-center justify-center gap-2">
                  <RotateCcw size={16} /> Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacebar hint - hidden on mobile */}
      <div className="hidden sm:block text-center py-2 bg-violet-500/10 border-b border-violet-500/20 flex-shrink-0">
        <span className="text-violet-400 text-xs font-medium tracking-wide">
          Press <kbd className="bg-white/10 px-2 py-0.5 rounded border border-white/20 font-mono text-[11px]">Space</kbd> to generate
        </span>
      </div>

      {/* Color bars - vertical on mobile, horizontal on desktop */}
      <div className="flex flex-1 overflow-auto md:overflow-hidden flex-col md:flex-row">
        {colors.map((c, i) => (
          <ColorBar key={i} index={i} color={c} locked={locked[i]}
            onToggleLock={toggleLock} onColorChange={(hex) => changeColor(i, hex)} 
            total={count} isVertical={typeof window !== 'undefined' && window.innerWidth < 768} />
        ))}
      </div>

      {/* Mobile generate FAB */}
      <button onClick={generate}
        className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-violet-500 to-violet-700 rounded-full shadow-xl shadow-violet-500/40 flex items-center justify-center text-white z-30">
        <RotateCcw size={22} />
      </button>
    </div>
  );
}